import { Fragment, useState, useEffect } from 'react'
import Layout from '../../components/Layout'
import api, { getOpenBetsForMarket, voidSingleOpenBet, deleteSportsBet } from '../../services/api'
import { API_ENDPOINTS } from '../../config/api'
import SettlementModal from '../../components/SettlementModal'
import SettlementOpenBets from '../../components/SettlementOpenBets'
import SettlementVoidModal from '../../components/SettlementVoidModal'

const LIMIT = 100
const DEFAULT_OFFSET = 0

export default function MatchSettlement() {
  const [activeTab, setActiveTab] = useState('MO')
  const [matches, setMatches] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalShow, setModalShow] = useState(false)
  const [modalMode, setModalMode] = useState('declare')
  const [selectedMatch, setSelectedMatch] = useState(null)

  // Per-bet view: ek market ki saari open bets, aur unme se kisi ek ka void
  const [expandedRow, setExpandedRow] = useState(null)
  const [openBets, setOpenBets] = useState([])
  const [betsLoading, setBetsLoading] = useState(false)
  const [voidConfirm, setVoidConfirm] = useState(null)
  const [voidSubmitting, setVoidSubmitting] = useState(false)
  const [voidError, setVoidError] = useState('')
  // Wahi confirm dialog dono kaam karta hai. deleteMode = Delete Bet, jo void
  // ki tarah paisa wapas karta hai aur uske baad bet ki row + statement trail
  // mita deta hai. Delete transaction password maangta hai (void nahi maangta).
  const [deleteMode, setDeleteMode] = useState(false)
  const [txnPassword, setTxnPassword] = useState('')

  const fetchMatches = async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      const response = await api.get(API_ENDPOINTS.INTERNALSETTLE.MOMATCHES, {
        params: { limit: LIMIT, offset: DEFAULT_OFFSET }
      })
      if (response.data?.success && Array.isArray(response.data.data)) {
        setMatches(response.data.data)
      } else {
        setMatches([])
      }
    } catch (error) {
      if (!silent) console.error('Error fetching MO/BM matches:', error)
      setMatches([])
    } finally {
      if (!silent) setLoading(false)
    }
  }

  useEffect(() => {
    fetchMatches()
    const interval = setInterval(() => fetchMatches(true), 2000)
    return () => clearInterval(interval)
  }, [])

  const filteredMatches = matches.filter((m) => m.gameType === activeTab)

  const handleDeclare = (row) => {
    setSelectedMatch(row)
    setModalMode('declare')
    setModalShow(true)
  }

  const handleVoid = (row) => {
    setSelectedMatch(row)
    setModalMode('void')
    setModalShow(true)
  }

  const handleSettlementSubmit = async () => {
    setModalShow(false)
    setSelectedMatch(null)
    fetchMatches(true)
  }

  const getRowKey = (row) => `${row.matchId}-${row.marketType}-${row.gameType}`
  const isExpanded = (row) => expandedRow === getRowKey(row)

  const handleExpand = async (row) => {
    const key = getRowKey(row)
    if (expandedRow === key) {
      setExpandedRow(null)
      setOpenBets([])
      return
    }
    setExpandedRow(key)
    setBetsLoading(true)
    try {
      const resp = await getOpenBetsForMarket({
        match_id: row.matchId,
        market_type: row.marketType,
        game_type: row.gameType
      })
      setOpenBets(resp?.success ? resp.data : [])
    } catch {
      setOpenBets([])
    } finally {
      setBetsLoading(false)
    }
  }

  const handleVoidSingleBet = (bet) => {
    setVoidError('')
    setTxnPassword('')
    setDeleteMode(false)
    setVoidConfirm(bet)
  }

  const handleDeleteSingleBet = (bet) => {
    setVoidError('')
    setTxnPassword('')
    setDeleteMode(true)
    setVoidConfirm(bet)
  }

  const confirmVoidSingle = async () => {
    if (!voidConfirm) return
    setVoidSubmitting(true)
    setVoidError('')
    try {
      const resp = deleteMode
        ? await deleteSportsBet({ bet_id: voidConfirm.id, transactionPassword: txnPassword })
        : await voidSingleOpenBet({ bet_id: voidConfirm.id })
      if (resp?.success) {
        setVoidConfirm(null)
        setDeleteMode(false)
        setTxnPassword('')
        setExpandedRow(null)
        setOpenBets([])
        fetchMatches(true)
      } else {
        setVoidError(resp?.error || 'Unknown error')
      }
    } catch (err) {
      setVoidError(err?.response?.data?.error || err.message)
    } finally {
      setVoidSubmitting(false)
    }
  }

  return (
    <Layout title="Match Settlement - Diamond Admin">
      <div className="listing-grid">
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-flex align-items-center justify-content-between">
              <h4 className="mb-0 font-size-18">Match Settlement</h4>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => fetchMatches()}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === 'MO' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('MO'); setExpandedRow(null); setOpenBets([]) }}
                >
                  Match Odds
                </button>
              </li>
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link ${activeTab === 'BM' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('BM'); setExpandedRow(null); setOpenBets([]) }}
                >
                  Bookmaker
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-bordered table-sm mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="text-start">Match Title</th>
                        <th className="text-start">Market Type</th>
                        <th className="text-end">Total Bets</th>
                        <th className="text-center" style={{ minWidth: '240px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={4} className="text-center py-4">
                            Loading...
                          </td>
                        </tr>
                      ) : filteredMatches.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="text-center py-4">
                            No matches found
                          </td>
                        </tr>
                      ) : (
                        filteredMatches.map((row, index) => (
                          <Fragment key={`${row.matchId}-${row.eventId}-${row.gameType}-${index}`}>
                            <tr>
                              <td className="text-truncate" style={{ maxWidth: '200px' }}>
                                {row.matchTitle ?? `${row.teamOne} vs ${row.teamTwo}`}
                              </td>
                              <td className="fw-bold text-truncate" style={{ maxWidth: '150px' }}>
                                {row.marketType || '-'}
                              </td>
                              <td className="text-end fw-bold">
                                {row.totalBets ?? 0}
                              </td>
                              <td className="text-center">
                                <div className="btn-group btn-group-sm">
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => handleDeclare(row)}
                                  >
                                    Declare
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => handleVoid(row)}
                                  >
                                    Void
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-warning"
                                    onClick={() => handleExpand(row)}
                                  >
                                    {isExpanded(row) ? 'Hide' : 'Per Bet'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {isExpanded(row) && (
                              <tr>
                                <td colSpan={4} className="bg-light p-3">
                                  <SettlementOpenBets
                                    label={row.marketType}
                                    bets={openBets}
                                    loading={betsLoading}
                                    onVoidBet={handleVoidSingleBet}
                                    onDeleteBet={handleDeleteSingleBet}
                                  />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <SettlementModal
        show={modalShow}
        onHide={() => {
          setModalShow(false)
          setSelectedMatch(null)
        }}
        mode={modalMode}
        match={selectedMatch}
        onSubmit={handleSettlementSubmit}
      />

      <SettlementVoidModal
        show={!!voidConfirm}
        title={deleteMode ? 'Delete Bet — permanent' : 'Void Single Bet'}
        onHide={() => setVoidConfirm(null)}
        onConfirm={confirmVoidSingle}
        submitting={voidSubmitting}
        confirmLabel={deleteMode ? 'Delete Permanently' : 'Confirm Void'}
        requirePassword={deleteMode}
        password={txnPassword}
        onPasswordChange={setTxnPassword}
        error={voidError}
      >
        {voidConfirm && (
          <>
            <p className="mb-2">
              Void bet <strong>#{voidConfirm.id}</strong> for user <strong>{voidConfirm.username}</strong>?
            </p>
            <p className="mb-2">
              Selection: <strong>{voidConfirm.selectionName}</strong> · Stake:{' '}
              <strong>{Number(voidConfirm.stakeAmount || 0).toFixed(2)}</strong> · Odds:{' '}
              <strong>{voidConfirm.odds}</strong>
            </p>
            {deleteMode ? (
              <div className="alert alert-danger mb-0 py-2">
                The stake is refunded exactly like a void, and then{' '}
                <strong>the bet is deleted</strong> — it disappears from the user&apos;s bet
                history and statement completely. <strong>This cannot be undone.</strong> A copy
                is kept in the void audit and activity log only.
              </div>
            ) : (
              <p className="mb-0 text-muted">
                The stake will be refunded. This action cannot be undone.
              </p>
            )}
          </>
        )}
      </SettlementVoidModal>
    </Layout>
  )
}
